/**
 * Conflict resolver for dex entry merges (b19-dex-server-sync).
 *
 * MIRROR of cloudflare/src/lib/dexRecordsResolver.ts -- keep behaviour identical.
 *
 * RULE: LAST WRITE WINS. Ties go to incoming.
 *
 * WHY SOURCE NO LONGER RANKS (changed 2026-07-18)
 * ----------------------------------------------
 * This used to rank sources: a "user-confirmed" row (manual/trade) beat a
 * scan-derived one regardless of age. That one rule produced a family of
 * silent-data-loss bugs, because a STALE user row outranked fresh scan data
 * forever:
 *
 *   - Quick-update "untick" writes a {state:"unknown", source:"manual"}
 *     tombstone. Since manual outranked scan, clearing a dex IN ORDER TO
 *     RE-SCAN IT made it permanently unscannable — the client skipped the write,
 *     the server upsert skipped it as "manual-wins", and the next pull reverted
 *     any local success back to "unknown".
 *   - Generally, precedence forced the app to decide which KIND of input was
 *     more trustworthy, and every place that decision was implemented could
 *     (and did) disagree with the others.
 *
 * A hand edit and a scan are both just statements about the same cell. The most
 * RECENT statement is the truth; nothing else needs adjudicating. `source` is
 * still carried as provenance (useful in logs/telemetry) but must never again be
 * used to rank one write above another.
 */

export function resolveDexEntryConflict(
  // `source` is still accepted — call sites pass whole rows and it remains
  // useful as provenance — but is deliberately NOT consulted. See above.
  existing: { source: string; updatedAt: number },
  incoming: { source: string; updatedAt: number },
): "keep" | "replace" {
  return incoming.updatedAt >= existing.updatedAt ? "replace" : "keep";
}
